import { useCallback } from 'react';

import { createActionsHook, createHook } from 'react-sweet-state';

import {
	SUGGESTED_PROMPT_TILE_STREAM_MESSAGE_TYPE,
	type SuggestedPromptTile,
	type SuggestedPromptTileStreamMessage,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import {
	type AssistanceServiceInjectedParams,
	type AssistanceServiceInputParam,
	createHookWithAssistanceService,
} from '@atlassian/conversation-assistant-service/services/assistance-service/main';

import { getPrimaryPrompts } from './getPrimaryPrompts';
import { getSecondaryPrompts } from './getSecondaryPrompts';
import { PRIMARY_PROMPTS_COUNT } from './PRIMARY_PROMPTS_COUNT';
import { PROMPT_SUGGESTIONS_OFFSET } from './PROMPT_SUGGESTIONS_OFFSET';
import { SECONDARY_PROMPTS_COUNT } from './SECONDARY_PROMPTS_COUNT';
import { store } from './store';
import type { UsePromptSuggestionsReturn } from './types';
import { useThirdPartySuggestedPromptTile } from './useThirdPartySuggestedPromptTile';

const TOTAL_PROMPT_SUGGESTIONS_COUNT = PRIMARY_PROMPTS_COUNT + SECONDARY_PROMPTS_COUNT;

const parsePromptSuggestionsStream = async ({
	generator,
	maxTiles,
	onPrimaryPromptsReady,
	onSecondaryPromptsReady,
}: {
	generator: AsyncGenerator<SuggestedPromptTileStreamMessage>;
	/**
	 * Upper bound on tiles to read. Exceeds the requested `limit` when the backend adds
	 * the 3P tile on top of the prompts, so stopping at `limit` would drop it whenever it
	 * streams last.
	 */
	maxTiles: number;
	onPrimaryPromptsReady: (tiles: SuggestedPromptTile[]) => void;
	onSecondaryPromptsReady: (tiles: SuggestedPromptTile[]) => void;
}): Promise<SuggestedPromptTile[]> => {
	const tiles: SuggestedPromptTile[] = [];

	try {
		for await (const chunk of generator) {
			if (chunk.type !== SUGGESTED_PROMPT_TILE_STREAM_MESSAGE_TYPE) {
				continue;
			}

			tiles.push(chunk.message);

			if (tiles.length === PRIMARY_PROMPTS_COUNT) {
				onPrimaryPromptsReady(getPrimaryPrompts(tiles));
			}

			if (tiles.length === maxTiles) {
				onSecondaryPromptsReady(getSecondaryPrompts(tiles));
				break;
			}
		}
	} catch (error) {
		if (tiles.length === 0) {
			throw error;
		}
	} finally {
		// If stream returns < maxTiles (on either success or partial failure), return whatever we have
		if (tiles.length < PRIMARY_PROMPTS_COUNT) {
			onPrimaryPromptsReady(getPrimaryPrompts(tiles));
		}
		if (tiles.length < maxTiles) {
			onSecondaryPromptsReady(getSecondaryPrompts(tiles));
		}
	}

	return tiles.slice(0, maxTiles);
};

const useStore = createHook(store);

const useStoreActions = createActionsHook(store);

export const usePromptSuggestions: (
	props: AssistanceServiceInputParam,
) => UsePromptSuggestionsReturn = createHookWithAssistanceService(
	({ assistanceServiceInjectedParams }: AssistanceServiceInjectedParams) => {
		const { assistanceService, touchpointSource, product, experienceId, siteId, headers, baseUrl } =
			assistanceServiceInjectedParams;
		const [state] = useStore();
		const {
			getState,
			setLoading,
			setPrimaryPrompts,
			setSecondaryPrompts,
			setData,
			shuffleSuggestions,
			setError,
		} = useStoreActions();

		const { enable3pPrompt, ready: is3pReady } = useThirdPartySuggestedPromptTile({
			assistanceServiceParams: {
				touchpointSource,
				product,
				experienceId,
				siteId,
				headers,
				baseUrl,
			},
			tilesDisplayed: (state.primaryPrompts?.length ?? 0) > 0,
		});

		const loadSuggestions = useCallback(async () => {
			// Wait for the 3P signal to settle so we request a stable flag value (always
			// ready off a 3P surface); the host effect re-fires this when `is3pReady` flips.
			if (!is3pReady) {
				return;
			}

			const { isLoading, data, error, primaryPrompts, secondaryPrompts } = getState();
			if (isLoading || data || error || primaryPrompts || secondaryPrompts) {
				return;
			}

			setLoading();

			try {
				const { generator } = await assistanceService.fetchPersonalizedPromptSuggestionsStream({
					offset: PROMPT_SUGGESTIONS_OFFSET,
					limit: TOTAL_PROMPT_SUGGESTIONS_COUNT,
					enable3pPrompt,
				});

				const prompts = await parsePromptSuggestionsStream({
					generator,
					// The 3P tile comes on top of the `limit` prompts rather than inside them, so
					// the variants that asked for it must read one tile more than they requested.
					maxTiles: TOTAL_PROMPT_SUGGESTIONS_COUNT + (enable3pPrompt ? 1 : 0),
					onPrimaryPromptsReady: setPrimaryPrompts,
					onSecondaryPromptsReady: setSecondaryPrompts,
				});

				if (prompts.length === 0) {
					throw new Error('No prompt suggestions returned');
				}
				setData(prompts);
			} catch (err: unknown) {
				setError(err instanceof Error ? err : new Error('Failed to fetch prompt suggestions'));
			}
		}, [
			assistanceService,
			getState,
			setLoading,
			setPrimaryPrompts,
			setSecondaryPrompts,
			setData,
			setError,
			is3pReady,
			enable3pPrompt,
		]);

		return {
			...state,
			// Surfaced so the grid can hold the 3P tile's slot while it streams. Only this hook
			// knows the flag, because it combines cohort with connector eligibility.
			enable3pPrompt,
			loadSuggestions,
			shuffleSuggestions,
		};
	},
);
